import { useEffect, useRef, useState } from "react";
import { billingApi, creditsApi } from "api";
import cn from "classnames";
import type { UseCredits } from "hooks/useCredits";
import { useFlash } from "hooks/useFlash";
import { getErrorMessage, isUnauthenticated } from "utils/error.utils";
import { AutoTopupCard, FailedPaymentAlert } from "./AutoTopupCard";
import { BalanceCard } from "./BalanceCard";
import { BuyCard } from "./BuyCard";
import { CreditActivityCard } from "./CreditActivityCard";
import type { BuyMode, CheckoutReturn } from "./credits.model";
import { billingView, readCheckoutReturn, stripCheckoutReturn } from "./credits.model";
import { InvoicesTable } from "./InvoicesTable";
import { PriceListCard } from "./PriceListCard";

type BillingPaneProps = {
  credits: UseCredits;
  onSessionLost: () => void;
};

/** Stripe's webhook can land after the browser does, so a success reads again. */
const WEBHOOK_GRACE_MS = 4000;

const RETURN_NOTE: Record<CheckoutReturn, string> = {
  success: "Payment received. Your QCredits show here as soon as Stripe confirms it.",
  cancelled: "Checkout was cancelled. Nothing was charged.",
};

type Note = { text: string; tone: "ok" | "bad" | "plain" };

/**
 * `#ov-bill`, Plan & Billing: the QCredits balance, the monthly auto top-up,
 * the calculator, the month-by-month activity and Stripe's invoices. A company
 * that has bought nothing yet sees its free credits and the price list.
 *
 * Credits are only ever added by Stripe's webhook, so everything here reads
 * the back office again after an action instead of trusting its own sums.
 */
export const BillingPane = ({ credits, onSessionLost }: BillingPaneProps) => {
  const { state, pricing, invoices, status, reload } = credits;
  const [buyMode, setBuyMode] = useState<BuyMode>("once");
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  const [version, setVersion] = useState(0);
  const { shown, flash } = useFlash();
  const consumed = useRef(false);
  const buyRef = useRef<HTMLDivElement>(null);

  const refresh = () => {
    reload();
    setVersion((current) => current + 1);
  };

  // Opening the pane is when its figures may have moved on.
  useEffect(() => reload(), [reload]);

  // Back from Stripe Checkout: say how it went, read again, tidy the address bar.
  useEffect(() => {
    if (consumed.current) return undefined;
    consumed.current = true;

    const back = readCheckoutReturn(window.location.search);

    if (back === null) return undefined;

    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${stripCheckoutReturn(window.location.search)}${window.location.hash}`
    );
    setNote({ text: RETURN_NOTE[back], tone: back === "success" ? "ok" : "plain" });
    flash();

    if (back !== "success") return undefined;

    const timer = setTimeout(refresh, WEBHOOK_GRACE_MS);

    return () => clearTimeout(timer);
    // Runs once on mount by design; `consumed` guards a repeated effect in dev.
  }, []);

  const tell = (text: string, tone: Note["tone"]) => {
    setNote({ text, tone });
    flash();
  };

  const fail = (caught: unknown, fallback: string) => {
    if (isUnauthenticated(caught)) {
      onSessionLost();
      return;
    }

    tell(getErrorMessage(caught, fallback), "bad");
  };

  const openBuy = (mode: BuyMode) => {
    setBuyMode(mode);
    requestAnimationFrame(() => buyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const submit = async (amount: number, autoTopup: boolean) => {
    setBusy(true);

    try {
      if (buyMode === "change") {
        await creditsApi.changeAutoTopup(amount);
        setBuyMode("once");
        tell("Auto top-up updated. The new amount applies from the next charge.", "ok");
        refresh();
        setBusy(false);
        return;
      }

      const { data } = await creditsApi.checkout({ credits: amount, auto_topup: autoTopup });

      window.location.assign(data.url);
    } catch (caught: unknown) {
      fail(
        caught,
        buyMode === "change" ? "Could not update the auto top-up." : "Could not open checkout. Please try again."
      );
      setBusy(false);
    }
  };

  const toggleAutoTopup = async (on: boolean) => {
    setBusy(true);

    try {
      await (on ? creditsApi.resumeAutoTopup() : creditsApi.cancelAutoTopup());
      tell(on ? "Auto top-up is back on." : "Auto top-up turned off. Your QCredits stay on your balance.", "ok");
      refresh();
    } catch (caught: unknown) {
      fail(caught, "Could not change the auto top-up. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);

    try {
      const { data } = await billingApi.portal();

      window.location.assign(data.url);
    } catch (caught: unknown) {
      fail(caught, "Could not open the billing portal. Please try again.");
      setPortalBusy(false);
    }
  };

  if (status === "loading" && state === null) {
    return (
      <div className="pc-pane">
        <div className="pcx">
          <span className="pc-spin" aria-label="Loading" />
        </div>
      </div>
    );
  }

  if (state === null || pricing === null) {
    return (
      <div className="pc-pane">
        <div className="pcx">
          <p className="pc-note pc-note--bad">
            {status === "unverified"
              ? "Verify your email address to see your QCredits."
              : "Could not load your QCredits. Please refresh the page."}
          </p>
        </div>
      </div>
    );
  }

  const view = billingView(state);
  const auto = view.auto;
  // With an auto top-up running, the calculator waits behind "Buy extra" / "Change amount".
  const showBuy = auto === null || buyMode !== "once";

  return (
    <div className="pc-pane">
      <div className="pc-keys-bill">
        {note !== null && shown && (
          <p
            className={cn("pc-note", "pc-bill__note", {
              "pc-note--ok": note.tone === "ok",
              "pc-note--bad": note.tone === "bad",
            })}
            role="status"
          >
            {note.text}
          </p>
        )}

        <div className="acr">
          {auto?.status === "failed" && (
            <FailedPaymentAlert topUp={auto.topUp} portalBusy={portalBusy} onOpenPortal={openPortal} />
          )}

          <BalanceCard state={state} view={view} />

          {auto !== null && (
            <AutoTopupCard
              view={auto}
              balance={state.balance.total}
              busy={busy}
              portalBusy={portalBusy}
              onOpenBuy={openBuy}
              onTurnOff={() => toggleAutoTopup(false)}
              onResume={() => toggleAutoTopup(true)}
              onOpenPortal={openPortal}
            />
          )}

          {showBuy && (
            <div ref={buyRef}>
              <BuyCard
                pricing={pricing}
                auto={auto?.topUp ?? null}
                mode={auto === null ? "once" : buyMode}
                busy={busy}
                onClose={() => setBuyMode("once")}
                onSubmit={submit}
              />
            </div>
          )}

          {!view.empty && <CreditActivityCard version={version} onSessionLost={onSessionLost} />}

          {!view.empty && invoices !== null && invoices.length > 0 && (
            <InvoicesTable invoices={invoices} portalBusy={portalBusy} onOpenPortal={openPortal} />
          )}

          {view.empty && <PriceListCard products={pricing.products} />}
        </div>
      </div>
    </div>
  );
};
